import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  CreateTradeableObjectPayload,
  UpdateTradeableObjectPayload,
  TradeableObjectMutationResponse,
} from '@/client/types/promotion-pricing';
import { promotionPricingKeys } from './promotion-pricing.query';
import { tradeableCatalogKeys } from './tradeable-catalog.query';

/** Created/updated tradeable object as returned by the mutation endpoints. */
export interface TradeableObjectResult {
  toid: number;
  uuid?: string;
  [k: string]: unknown;
}

/** Refetch every list that could show the mutated plan. */
const invalidateCatalogLists = (
  queryClient: ReturnType<typeof useQueryClient>,
): void => {
  queryClient.invalidateQueries({ queryKey: tradeableCatalogKeys.lists() });
  queryClient.invalidateQueries({ queryKey: promotionPricingKeys.lists() });
};

// ─── Create tradeable object ────────────────────────────────────────────────────

const useCreateTradeableObjectFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (payload: CreateTradeableObjectPayload): Promise<TradeableObjectResult> => {
      const response = await request<TradeableObjectMutationResponse>(
        '/api/admin/tradeable_objects/',
        'POST',
        payload,
      );
      return response.data;
    },
    'Failed to create plan',
  );
};

export const useCreateTradeableObjectMutation = () => {
  const queryClient = useQueryClient();
  const createFn = useCreateTradeableObjectFn();
  return useMutation({
    mutationFn: (payload: CreateTradeableObjectPayload) => createFn(payload),
    onSuccess: () => invalidateCatalogLists(queryClient),
  });
};

// ─── Update tradeable object ────────────────────────────────────────────────────

const useUpdateTradeableObjectFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (toid: number, payload: UpdateTradeableObjectPayload): Promise<TradeableObjectResult> => {
      const response = await request<TradeableObjectMutationResponse>(
        `/api/admin/tradeable_objects/${toid}`,
        'PUT',
        payload,
      );
      return response.data;
    },
    'Failed to update plan',
  );
};

export const useUpdateTradeableObjectMutation = () => {
  const queryClient = useQueryClient();
  const updateFn = useUpdateTradeableObjectFn();
  return useMutation({
    mutationFn: ({ toid, payload }: { toid: number; payload: UpdateTradeableObjectPayload }) =>
      updateFn(toid, payload),
    onSuccess: () => invalidateCatalogLists(queryClient),
  });
};

// ─── Delete tradeable object (soft-delete: active=0, deleted=1) ──────────────────

const useDeleteTradeableObjectFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (toid: number): Promise<{ status?: string }> => {
      const response = await request<{ status: number; data: { status?: string } }>(
        `/api/admin/tradeable_objects/${toid}`,
        'DELETE',
      );
      return response.data;
    },
    'Failed to delete plan',
  );
};

export const useDeleteTradeableObjectMutation = () => {
  const queryClient = useQueryClient();
  const deleteFn = useDeleteTradeableObjectFn();
  return useMutation({
    mutationFn: ({ toid }: { toid: number }) => deleteFn(toid),
    onSuccess: () => invalidateCatalogLists(queryClient),
  });
};
